import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

// components
import { Slider } from 'primereact/slider'
import { InputSwitch } from 'primereact/inputswitch'

import ToggleTriplet from '@aytm/app/stats/components/toggle-triplet/ToggleTriplet'

// styles
import style from '@aytm/app/stats/page/question-views/components/controls/styles/Controls.module.scss'
import { NO_PRINT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const parseMode = (mode = 'pba') => {
  return {
    first: mode.charAt(0) || 'p', // 'd' | 'p'
    second: mode.charAt(1) || 'b', // 'b' | 'd'
    stat: mode.charAt(2) || 'a' // 'a' | 'm'
  }
}

const buildMode = ({ first, second, stat }) => ('d' === first ? 'd' : `p${second}${stat}`)

const withSecond = (mode, second) => buildMode({ ...parseMode(mode), second })

const Controls = ({ neutral = 0, mode = 'pba', degree = 201, onModelChange }) => {
  const [neutralLocal, setNeutralLocal] = useState(neutral)

  const { first, second, stat } = useMemo(() => parseMode(mode), [mode])

  // Remember the last intensity family/metric so re-entering Intensity from Popularity
  // restores the prior choice (e.g. Deltas/Median) instead of resetting to Pairs/Mean.
  const lastIntensity = useRef({ second: 'b', stat: 'a' })
  useEffect(() => {
    if ('p' === first) lastIntensity.current = { second, stat }
  }, [first, second, stat])

  // Neutral threshold ceiling: one less than the integer half of the scale degree
  // (e.g. 7-point scale → 2). When degree is absent the renderer's 201 sentinel yields 99.
  const neutralMax = useMemo(() => Math.max(0, Math.floor(degree / 2) - 1), [degree])

  const onSlideChange = event => setNeutralLocal(event.value)
  const onSlideEnd = event => {
    if (event.value !== neutral) {
      onModelChange?.({ neutral: event.value })
    }
  }

  const setFirst = useCallback(
    v => {
      const next =
        'd' === v
          ? 'd'
          : buildMode({ first: 'p', second: lastIntensity.current.second, stat: lastIntensity.current.stat })
      if (next !== mode) onModelChange?.({ mode: next })
    },
    [mode, onModelChange]
  )

  const setSecond = useCallback(
    v => {
      const next = withSecond(mode, v)

      if (next !== mode) {
        onModelChange?.({ mode: next })
      }
    },
    [mode, onModelChange]
  )

  useEffect(() => setNeutralLocal(neutral), [neutral])

  return (
    <div className={`${style.component} ${NO_PRINT}`}>
      <ToggleTriplet
        name="psc-family"
        leftLabel="Popularity"
        rightLabel="Intensity"
        activeLeft={'d' == first}
        onLeftClick={() => setFirst('d')}
        onRightClick={() => setFirst('p')}
      >
        <InputSwitch
          aria-label="Intensity"
          checked={'p' == first}
          onChange={event => setFirst(event.value ? 'p' : 'd')}
        />
      </ToggleTriplet>

      {'p' == first ? (
        <ToggleTriplet
          name="psc-pairing"
          leftLabel="Pairs"
          rightLabel="Deltas"
          activeLeft={'b' == second}
          onLeftClick={() => setSecond('b')}
          onRightClick={() => setSecond('d')}
        >
          <InputSwitch
            aria-label="Deltas"
            checked={'d' == second}
            onChange={event => setSecond(event.value ? 'd' : 'b')}
          />
        </ToggleTriplet>
      ) : null}

      {'d' == first ? (
        <>
          <div className={`${style.slider} p-fluid p-col-4`}>
            <Slider
              aria-label="Count as Neutral"
              min={0}
              max={neutralMax}
              value={neutralLocal}
              labelTemplate={value => `±${value}`}
              onChange={onSlideChange}
              onSlideEnd={onSlideEnd}
            />
          </div>
          <div className="p-col">
            <span className={style['slider-title']}>
              Count as
              <br />
              Neutral
            </span>
          </div>
        </>
      ) : null}
    </div>
  )
}

Controls.displayName = 'Controls'

Controls.propTypes = {
  neutral: PropTypes.number,
  mode: PropTypes.string,
  degree: PropTypes.number,
  onModelChange: PropTypes.func
}

export default Controls
